import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-red-600 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Terms of Service</h1>
          </div>

          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 mb-8">
              By accessing or using CanadaTestPrep (the "Service"), you agree to these Terms of Service.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">1. Independent Platform</h2>
            <p className="text-slate-600 mb-4">
              CanadaTestPrep is an independent educational platform and is not affiliated with Immigration, Refugees and Citizenship Canada (IRCC) or the Government of Canada.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">2. Educational Purpose</h2>
            <p className="text-slate-600 mb-4">The Service provides:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Practice questions</li>
              <li>Mock examinations</li>
              <li>Chapter tests</li>
              <li>Simulations</li>
              <li>Flashcards</li>
              <li>Study materials</li>
            </ul>
            <p className="text-slate-600 mb-4">
              All materials are provided for educational preparation purposes only.
            </p>
            <p className="text-slate-600 mb-4 font-semibold">
              We do not provide official test questions or government services.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">3. No Government Guarantee</h2>
            <p className="text-slate-600 mb-4">We do not guarantee:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Citizenship approval</li>
              <li>Specific test questions</li>
              <li>Government decisions</li>
              <li>Immigration processing timelines</li>
            </ul>
            <p className="text-slate-600 mb-4 font-semibold">
              Official outcomes are determined solely by IRCC.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">4. Pass Guarantee (Lifetime Access Only)</h2>
            <p className="text-slate-600 mb-4">
              <strong>What is the Pass Guarantee?</strong> If you complete all practice tests, simulations, and mock exams on MaplePass but still don't pass your citizenship test, we'll refund your payment in full.
            </p>
            <p className="text-slate-600 mb-4 font-semibold">
              The Pass Guarantee applies only to users who purchase Lifetime Access.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">4.1 Eligibility Requirements</h3>
            <p className="text-slate-600 mb-4">To qualify for a refund:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Lifetime Access must be purchased.</li>
              <li>100% of all study modules must be completed.</li>
              <li>All chapter tests must be completed.</li>
              <li>All mock examinations must be completed.</li>
              <li>All simulations must be completed.</li>
              <li>All flashcards must be marked as reviewed within the system.</li>
              <li>A minimum score of 85% must be achieved on three separate full-length mock examinations prior to the official test.</li>
              <li>The official citizenship test must be taken within 60 days of completing the study program.</li>
              <li>A refund request must be submitted within 30 days of the official test date.</li>
              <li>Official documentation confirming the test result must be provided.</li>
            </ul>
            <p className="text-slate-600 mb-4">
              Completion is determined solely by system-recorded progress data.
            </p>
            <p className="text-slate-600 mb-4 font-semibold">
              Refunds are strictly limited to the amount paid for Lifetime Access.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">5. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">
              To the maximum extent permitted by law, CanadaTestPrep's total liability under any circumstance is limited to the amount paid for the applicable product tier.
            </p>
            <p className="text-slate-600 mb-4">We are not responsible for:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Government application fees</li>
              <li>Travel expenses</li>
              <li>Test scheduling or rescheduling fees</li>
              <li>Immigration delays</li>
              <li>Lost opportunities</li>
              <li>Emotional distress</li>
              <li>Indirect, incidental, or consequential damages</li>
            </ul>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">6. Lifetime Access Definition</h2>
            <p className="text-slate-600 mb-4">
              "Lifetime Access" means access to the Service for as long as CanadaTestPrep continues to operate and provide the platform.
            </p>
            <p className="text-slate-600 mb-4 font-semibold">
              It does not mean lifetime of the user or guaranteed perpetual operation of the platform.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">7. Marketing and Promotional Statements</h2>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>References to user counts (e.g., "1500+ users") reflect cumulative engagement and may include registered users, paid users, trial users, beta participants, and individuals who consulted with the platform creators.</li>
              <li>Testimonials reflect individual experiences and do not guarantee similar outcomes.</li>
              <li>Statements such as "#1 platform" are promotional language and do not represent certified rankings or official endorsements.</li>
              <li>Practice questions are designed to reflect the structure and subject matter of the official test based on publicly available materials. They are not official test questions.</li>
            </ul>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">8. User Conduct</h2>
            <p className="text-slate-600 mb-4">Users agree not to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Share login credentials</li>
              <li>Reproduce or redistribute platform content</li>
              <li>Misuse the Service</li>
            </ul>
            <p className="text-slate-600 mb-4 font-semibold">
              Violation may result in termination of access.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">9. Intellectual Property</h2>
            <p className="text-slate-600 mb-4">
              All content, materials, branding, and software are the property of CanadaTestPrep and protected under applicable intellectual property laws.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">10. Governing Law</h2>
            <p className="text-slate-600 mb-4">
              These Terms are governed by the laws of the Province of British Columbia and the applicable federal laws of Canada.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">11. Contact</h2>
            <p className="text-slate-600 mb-4">
              For questions regarding these Terms, please contact us through our{" "}
              <Link to="/contact" className="text-red-600 hover:underline">contact form</Link>.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm">
          <p>© 2026 CanadaTestPrep.ca. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
